import { FlexColumn, FlexRow } from "Components/Flex";
import Popover from "Components/Popover";
import { Button } from "melodies-source/Button";
import styled from "styled-components";
import { ReactComponent as RefreshIcon } from "assets/svg/refresh.svg";
import { ReactComponent as ShowIcon } from "assets/svg/show.svg";
import { ReactComponent as TrashIcon } from "assets/svg/trash.svg";
import { InstantMessage as InstantMessageIcon } from "svg/InstantMessage";
import { Fragment, useState } from "react";
import { useScrollLockContext } from "contexts/ScrollLockContext";
import { Badge } from "Components/Badge";
import { Tooltip } from "melodies-source/Tooltip";
import Skeleton from "react-loading-skeleton";
import { MessageModal } from "./MessageModal";
import {
  useDocument,
  useDocumentDataOnce,
} from "react-firebase-hooks/firestore";
import { Ref } from "refs";
import { useSetfanSweeps } from "./hooks/useSetfanSweeps";
import { WinnerDetailModal } from "./WinnerDetailsModal";
import { RemoveWinnerModal, ReplaceWinnerModal } from "./ActionModal";
import { Conversation } from "@max/common/dist/api/communication/conversations";
import { useArtistContext } from "Components";

export type WinnerConversation = Conversation & {
  id: `${string}#${string}` | string;
};

export const WinnerRow = ({
  conversation,
  fanId,
  surveyId,
}: {
  conversation?: WinnerConversation;
  fanId: string;
  surveyId: string;
}) => {
  const [modalOpen, setModalOpen] = useState<
    "details" | "message" | "remove" | "replace" | null
  >(null);

  const { lockScroll, unlockScroll } = useScrollLockContext();

  const { id: groupId } = useArtistContext();

  const { replaceWinner, winnerBeingReplaced, removeWinner, activeSweeps } =
    useSetfanSweeps({ surveyId });

  const [winningFanEntryRef, loadingFanEntry] = useDocument(
    Ref("sts3_surveys/{surveyId}/sweeps_entries/{entryId}", {
      surveyId,
      entryId: fanId,
    }),
  );

  const [fanDoc, loadingFan] = useDocumentDataOnce(
    Ref("artist_groups/{groupId}/fans/{fanId}", { groupId, fanId }),
  );

  if (loadingFanEntry || loadingFan || !winningFanEntryRef.exists) {
    return <WinnerRowLoader />;
  }
  const winningFan = {
    id: winningFanEntryRef.id,
    isRemoved: activeSweeps?.removedFans?.includes(winningFanEntryRef.id),
    // the fan doc should always exist, but fallback just in case
    displayName: fanDoc?.name || "N/A",
    ...winningFanEntryRef.data(),
  };

  return (
    <Fragment key={winningFan.id}>
      <FlexRow
        key={winningFan.id}
        style={{
          justifyContent: "space-between",
          paddingTop: 21,
          paddingBottom: 21,
          alignItems: "flex-end",
        }}
      >
        {winningFan.isRemoved ? (
          <>
            <ReplaceButton
              onClick={async () => await replaceWinner(winningFan.id)}
            >
              Replace
            </ReplaceButton>
          </>
        ) : winnerBeingReplaced === winningFan.id ? (
          <WinnerRowLoader />
        ) : (
          <>
            <FlexColumn>
              <FiltersLabelText>{winningFan.displayName}</FiltersLabelText>
              <FiltersLabelSubtext>
                {winningFan.countryCode}
              </FiltersLabelSubtext>
            </FlexColumn>
            <FlexRow>
              <Badge
                value={conversation?.unreadCount ?? 0}
                onClick={() => {
                  if (!winningFan.canSms) return;
                  lockScroll();
                  setModalOpen("message");
                }}
              >
                {winningFan.canSms ? (
                  <StyledInstantMessageIcon canSms={winningFan.canSms} />
                ) : (
                  <Tooltip
                    parent={
                      <StyledInstantMessageIcon canSms={winningFan.canSms} />
                    }
                  >
                    <div style={{ width: 200 }}>
                      This user is unable to be contacted by sms.
                    </div>
                  </Tooltip>
                )}
              </Badge>
              <StyledPopover forceSide="left">
                <FlexColumn style={{ overflow: "hidden" }}>
                  <WinnerPopoverRow
                    onClick={() => {
                      lockScroll();
                      setModalOpen("replace");
                    }}
                  >
                    <RefreshIcon style={{ marginRight: 10 }} />
                    <WinnerPopoverLabel>Replace Winner</WinnerPopoverLabel>
                  </WinnerPopoverRow>
                  <WinnerPopoverRow
                    onClick={() => {
                      lockScroll();
                      setModalOpen("details");
                    }}
                  >
                    <ShowIcon style={{ marginRight: 10 }} />
                    <WinnerPopoverLabel>Winner Details</WinnerPopoverLabel>
                  </WinnerPopoverRow>
                  <WinnerPopoverRow
                    onClick={() => {
                      lockScroll();
                      setModalOpen("remove");
                    }}
                  >
                    <TrashIcon style={{ marginRight: 10 }} />
                    <WinnerPopoverLabel>Remove</WinnerPopoverLabel>
                  </WinnerPopoverRow>
                </FlexColumn>
              </StyledPopover>
            </FlexRow>
          </>
        )}
      </FlexRow>
      <WinnerDetailModal
        isOpen={modalOpen === "details"}
        onClose={() => {
          unlockScroll();
          setModalOpen(null);
        }}
        surveyId={surveyId}
        winnerDetails={winningFan}
      />
      <MessageModal
        conversationId={conversation?.id}
        isOpen={modalOpen === "message"}
        onClose={() => {
          unlockScroll();
          setModalOpen(null);
        }}
        winningFan={winningFan}
      />
      <ReplaceWinnerModal
        isOpen={modalOpen === "replace"}
        onClose={() => {
          unlockScroll();
          setModalOpen(null);
        }}
        onConfirm={() => replaceWinner(fanId)}
        winnerName={winningFan.displayName}
      />
      <RemoveWinnerModal
        isOpen={modalOpen === "remove"}
        onClose={() => {
          unlockScroll();
          setModalOpen(null);
        }}
        onConfirm={() => removeWinner(fanId)}
        winnerName={winningFan.displayName}
      />
    </Fragment>
  );
};

const StyledInstantMessageIcon = styled(InstantMessageIcon)<{
  canSms: boolean;
}>`
  height: 29px;
  width: 29px;
  cursor: ${({ canSms }) => (canSms ? "pointer" : "not-allowed")};
  position: relative;
  & > path {
    fill: ${({ canSms }) => (canSms ? "#1b0076" : "#c9c9c9")};
  }
`;

const WinnerRowLoader = () => {
  return (
    <>
      <FlexColumn>
        <FiltersLabelText>
          <Skeleton width={100} containerClassName="flex-1" />
        </FiltersLabelText>
        <FiltersLabelSubtext>
          <Skeleton width={100} containerClassName="flex-1" />
        </FiltersLabelSubtext>
      </FlexColumn>
      <FlexRow>
        <Skeleton
          height={27}
          width={27}
          containerClassName="flex-1"
          style={{ marginRight: 15 }}
        />
        <Skeleton height={27} width={5} />
      </FlexRow>
    </>
  );
};

const FiltersLabelText = styled.div`
  color: #333;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
`;

const FiltersLabelSubtext = styled.div`
  color: #333;
  font-family: Poppins;
  font-size: 12px;
  line-height: 18px;
`;

const ReplaceButton = styled(Button)`
  background: transparent !important;
  border: 1px solid #1b0076;
  color: #1b0076;
  width: 123px;
  ${({ theme }) => theme.media.tablet} {
    width: 123px;
  }
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`;

const StyledPopover = styled(Popover)`
  margin-left: 15px;
`;

const WinnerPopoverRow = styled(FlexRow)`
  padding: 12px 15px;
  width: 180px;
  &:hover {
    background-color: #1b007615;
  }
`;

const WinnerPopoverLabel = styled.div`
  color: #333;
  font-family: Poppins;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
`;
